import { MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useRoomContextRef from './useRoomContextRef'
import { wsConnection } from './WSConnectionInstance'
import useUserContextRef from './useUserContextRef'
import { MusePeer, PeerManifest } from './RoomContextMusePeer'
import { logger } from './Logger'
import useRoomActions from './useRoomActions'
import { RoomContext, RoomContextType } from '../RoomContext'
import { sleep } from '../utils'
import { env } from './EnvironmentVariables'
import { WSConnectionSendError } from './WSConnectionManager'

const log = logger.createChildLogger({ name: 'SfuAddPeersToContext' })

type RoomMetadataResponse = {
    clients: {
        userUrn: string
        username: string
        displayName: string
        isGuest: boolean
        sessionId: string
        appSerialId: string
        appVersion: string
    }[]
}

export default function useSfuAddPeersToContextAction() {
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { leaveRoom, checkRemotePeersAudioEngineCompatibilityAndShowDialog } = useRoomActions()

    /**
     * a flag (for each sessionId) which indicates that the peer is currently being added
     * to the roomContext.
     */
    const addingPeerToRoomLookupRef = useRef<Set<string /*sessionId*/>>(new Set())

    const lastManifestRef = useRef<Map<string, PeerManifest>>(new Map())

    const latestRunId = useRef(0)

    const criticalSectionFlagRef = useRef(false)

    // Test jig for the TEST_WEBSOCKET_CONNECTION_DROP_AT_SFU_ADD_PEERS flag, to prevent dropping
    // multiple times per room.
    const _testWebsocketConnectionDroppedCtRef = useRef(0)

    // Test jig for TEST_SFU_ADD_PEERS_RESPONSE_CLENTS_MISSING
    const _testSfuAddPeersResponseClientsMissingCtRef = useRef(0)

    const fs = useMemo(() => {
        const enterCriticalSection = async (mRef: MutableRefObject<boolean>) => {
            for (let i = 0; i < 50; ++i) {
                if (mRef.current === false) {
                    break
                }
                await sleep(50)
            }
            mRef.current = true
        }

        const exitCriticalSection = (mRef: MutableRefObject<boolean>) => {
            mRef.current = false
        }

        const mergePeersToRoomState = async (
            runId: number,
            sessionIds: Set<string>,
            response: RoomMetadataResponse
        ): Promise<string[]> => {
            const { sfuRoom } = roomStateRef.current
            if (!sfuRoom) {
                return []
            }

            if (env.TEST_SFU_ADD_PEERS_RESPONSE_CLENTS_MISSING) {
                const ct = parseInt(env.TEST_SFU_ADD_PEERS_RESPONSE_CLENTS_MISSING)
                if (_testSfuAddPeersResponseClientsMissingCtRef.current < ct) {
                    _testSfuAddPeersResponseClientsMissingCtRef.current += 1
                    response.clients = []
                }
            }

            const upsertPeers: { [key: string]: MusePeer } = {}
            response.clients
                .filter((a: any) => sessionIds.has(a.sessionId))
                .forEach((a: any) => {
                    let peerObj = null
                    if (roomStateRef.current.peers[a.sessionId]) {
                        peerObj = { ...roomStateRef.current.peers[a.sessionId] }
                    } else {
                        peerObj = MusePeer({
                            userUrn: a.userUrn,
                            username: a.username,
                            displayName: a.displayName,
                            isGuest: a.isGuest ?? false,
                            sessionId: a.sessionId,
                            appSerialId: a.appSerialId,
                            appVersion: a.appVersion,
                            clientType: a.clientType || '',
                            callInitiator: 'them',
                        })
                    }

                    peerObj.userUrn = a.userUrn
                    peerObj.username = a.username
                    peerObj.displayName = a.displayName
                    peerObj.isGuest = a.isGuest ?? false
                    peerObj.sessionId = a.sessionId
                    peerObj.appSerialId = a.appSerialId

                    peerObj.connectionStatus = 'connected'
                    peerObj.sfuConnection = sfuRoom

                    const lastManifest = lastManifestRef.current.get(a.sessionId)
                    if (lastManifest) {
                        peerObj.manifest = lastManifest
                    }

                    upsertPeers[a.sessionId] = peerObj
                })

            log.info('sfuAddPeersToContext', `(runId=${runId}) dispatch newPeers`, {
                newPeers: Object.keys(upsertPeers),
            })

            roomDispatch({
                type: 'upsert_peers',
                value: upsertPeers,
            })

            // check audioengine compatibility
            checkRemotePeersAudioEngineCompatibilityAndShowDialog(
                userStateRef.current.mediaStreams.audioInputs,
                upsertPeers
            )

            const upsertedSessionIds = Object.keys(upsertPeers)
            return upsertedSessionIds
        }

        /**
         * This function should NOT be interleaved.
         */
        const sfuAddPeersToContextHelper = async (sessionIds: string[]) => {
            const { sfuRoom } = roomStateRef.current
            if (!sfuRoom) {
                log.error('sfuAddPeersToContext', `sfuRoom is null!`)
                return
            }

            if (!sessionIds) {
                log.error('sfuAddPeersToContext', `sessionIds is null!`)
                return
            }

            sessionIds = sessionIds.filter((sessionId) => {
                const isTranscriber = sessionId.startsWith('agent-')
                if (isTranscriber) {
                    log.verbose('sfuAddPeersToContext', `Skipping adding transcriber.`, { sessionId })
                    return false // do not add
                } else {
                    return true
                }
            })

            if (sessionIds.length === 0) {
                log.verbose('sfuAddPeersToContext', `Not running because sessionIds is empty.`, { sessionIds })
                return
            }

            const runId = ++latestRunId.current

            log.info('sfuAddPeersToContext', `(runId=${runId}) Started.`, { sessionIds })

            sessionIds.forEach((sessionId) => {
                addingPeerToRoomLookupRef.current.add(sessionId)
            })

            let response: WSConnectionSendError | any = null
            try {
                const TIMEOUT_MS = 5000

                if (env.TEST_WEBSOCKET_CONNECTION_DROP_AT_SFU_ADD_PEERS) {
                    if (_testWebsocketConnectionDroppedCtRef.current === 0) {
                        setTimeout(() => {
                            wsConnection.testClose(10000)
                        }, 10)
                        _testWebsocketConnectionDroppedCtRef.current += 1
                    }
                }

                response = await wsConnection.sendMessage(
                    {
                        type: 'get-room-metadata',
                        data: {
                            sessionId: userStateRef.current.sessionId,
                            roomUrn: roomStateRef.current.roomUrn,
                        },
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                        tBetweenRetriesMs: TIMEOUT_MS,
                        totalTimeoutMs: TIMEOUT_MS,
                    }
                )
            } catch (e) {
                log.error(
                    'sfuAddPeersToContext',
                    `(runId=${runId}) 'an exception occurred on 'get-room-metadata' call.`,
                    { e }
                )
            }

            if (runId !== latestRunId.current) {
                // another run has started, so ignore this response.
                log.info(
                    'sfuAddPeersToContext',
                    `(runId=${runId}) Another run has started; ignoring this 'get-room-metadata' response.`,
                    { runId, latestRunId: latestRunId.current, sessionIds }
                )
                return
            }

            // if no response in TIMEOUT_MS, then assume connection is lost and close.
            if (response instanceof WSConnectionSendError) {
                if (response.code === 'response_timed_out') {
                    log.error(
                        'sfuAddPeersToContext',
                        `(runId=${runId}) 'get-room-metadata' response timed out. ` +
                            `Closing and restarting websocket connection.`
                    )
                    wsConnection.reconnect()
                    return
                } else {
                    // websocket is likely not connected. We will rely on the websocket reconnect
                    // to call join-room again when the connection is re-established.
                    log.error(
                        'sfuAddPeersToContext',
                        `(runId=${runId}) 'get-room-metadata' returned error. ` + `response.code=${response.code}.`
                    )
                    return
                }
            }

            if (!response?.clients || !Array.isArray(response.clients)) {
                log.assert('sfuAddPeersToContext', `(runId=${runId}) invalid response to 'get-room-metadata'.`, {
                    response,
                })
                return
            }

            // enter critical section
            await enterCriticalSection(criticalSectionFlagRef)
            log.info(
                'sfuAddPeersToContext',
                `(runId=${runId}) enter critical section. calling mergePeersToRoomState()`,
                {
                    runId,
                    sessionIds: [...addingPeerToRoomLookupRef.current],
                    response,
                }
            )

            const upsertedSessionIds = await mergePeersToRoomState(
                runId,
                new Set(addingPeerToRoomLookupRef.current),
                response as RoomMetadataResponse
            )

            upsertedSessionIds.forEach((sessionId) => {
                addingPeerToRoomLookupRef.current.delete(sessionId)
                addingPeerToRoomRetryMapRef.current.delete(sessionId)
            })

            log.info('sfuAddPeersToContext', `(runId=${runId}) exit critical section.`)
            exitCriticalSection(criticalSectionFlagRef)

            if (addingPeerToRoomLookupRef.current.size > 0) {
                log.info('sfuAddPeersToContext', `(runId=${runId}) There are still peers to add to the room.`, {
                    addingPeerToRoomLookupRef: [...addingPeerToRoomLookupRef.current],
                })
                setAddPeerRetry((ct) => ct + 1)
            }
        }

        const sfuAddPeersToContext = async (sessionIds: string[]) => {
            try {
                await sfuAddPeersToContextHelper(sessionIds)
            } catch (e) {
                exitCriticalSection(criticalSectionFlagRef)

                log.assert('sfuAddPeersToContext', `an unexpected exception occurred at sfuAddPeersToContext(). e=`, {
                    e,
                })
                const message = e instanceof Error ? e.message : ''

                sessionIds.forEach((sessionId) => {
                    addingPeerToRoomLookupRef.current.delete(sessionId)
                })

                const msg = `Failed to join the room due to an unexpected error. message="${message}".`
                leaveRoom({ endSession: false, showDialogWithMessage: msg })
                return
            }
        }

        const setLastManifest = (sessionId: string, manifest: PeerManifest) => {
            lastManifestRef.current.set(sessionId, manifest)
        }

        return {
            sfuAddPeersToContext,
            addingPeerToRoomLookupRef,
            setLastManifest,
        }
    }, [])

    // trigger running the addingPeerToRoomLookup retry routine.
    const [addPeerRetry, setAddPeerRetry] = useState(0)

    const addingPeerToRoomRetryMapRef = useRef<Map<string, number>>(new Map())

    useEffect(
        function addPeerRetryEffect() {
            let timer = setTimeout(() => {
                if (addingPeerToRoomLookupRef.current.size > 0) {
                    log.info('addPeerRetryEffect', `Calling sfuAddPeersToContext()`, {
                        addingPeerToRoomLookupRef: [...addingPeerToRoomLookupRef.current],
                        retryMap: [...addingPeerToRoomRetryMapRef.current],
                    })

                    // manage retries
                    addingPeerToRoomLookupRef.current.forEach((sessionId) => {
                        addingPeerToRoomRetryMapRef.current.set(
                            sessionId,
                            (addingPeerToRoomRetryMapRef.current.get(sessionId) ?? 0) + 1
                        )

                        if ((addingPeerToRoomRetryMapRef.current.get(sessionId) ?? 0) > 5) {
                            log.error(
                                'addPeerRetryEffect',
                                `sessionId has been retried too many times. ` + `Dropping it.`,
                                {
                                    sessionId,
                                    addingPeerToRoomLookupRef: [...addingPeerToRoomLookupRef.current],
                                    retryMap: [...addingPeerToRoomRetryMapRef.current],
                                }
                            )
                            addingPeerToRoomLookupRef.current.delete(sessionId)
                            addingPeerToRoomRetryMapRef.current.delete(sessionId)
                        }
                    })

                    fs.sfuAddPeersToContext([...addingPeerToRoomLookupRef.current]).then(() => {
                        log.info('addPeerRetryEffect', `sfuAddPeersToContext() returned.`, {
                            addingPeerToRoomLookupRef: [...addingPeerToRoomLookupRef.current],
                        })
                        // `sfuAddPeersToContext` manages `addingPeerToRoomLookupRef` and also sets
                        // `setAddPeerRetry` if there are still peers to add.
                    })
                }
            }, 3000)

            return () => {
                clearTimeout(timer)
            }
        },
        [addPeerRetry]
    )

    return fs
}
